import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Shield, ArrowRight } from 'lucide-react';
import { navigationLinks } from '../data/navigationData';
import { contactData } from '../data/contactData';
import Button from './Button';

export default function Navbar({ onOpenConsultation }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedDropdowns, setExpandedDropdowns] = useState({});
  const location = useLocation();

  // Scroll listener for sticky navbar transformation
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const toggleDropdown = (name) => {
    setExpandedDropdowns((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top micro-bar with 24/7 Helpline & Crisis support */}
      <div className="bg-[#123B5D] text-[#F5FAF9] text-xs py-2 px-4 sm:px-8 border-b border-[#0F766E]/30 hidden md:block">
        <div className="w-full px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-4 text-[#D9E8E5]">
            <span className="flex items-center gap-1.5 font-medium text-white">
              <Shield className="w-3.5 h-3.5 text-[#2A9D8F]" />
              NABH-Standard Clinical Care & Psychiatric Rehabilitation
            </span>
            <span className="text-[#0F766E]">|</span>
            <span>24/7 Emergency Psychiatric Admissions Open</span>
          </div>

          <div className="flex items-center gap-6">
            <a
              href={`tel:${contactData.phones[0].raw}`}
              className="flex items-center gap-1.5 text-[#F5FAF9] hover:text-[#2A9D8F] transition-colors"
            >
              <Phone className="w-3.5 h-3.5 text-[#2A9D8F]" />
              <span className="font-semibold">{contactData.phones[0].number}</span>
            </a>
            <a
              href={contactData.whatsapp.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#D9E8E5] hover:text-white transition-colors"
            >
              WhatsApp Desk
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation Header */}
      <header
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          isScrolled
            ? 'bg-white py-3 shadow-soft border-b border-[#D9E8E5]'
            : 'bg-white py-4 border-b border-[#D9E8E5]/60'
        }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4">
            
            {/* Brand Logo - Aligned to extreme left */}
            <Link to="/" className="flex items-center gap-3 group focus:outline-none flex-shrink-0 py-0.5">
              <img
                src="/umed logo.png"
                alt="UMED Rehabilitation Centre"
                className="h-12 sm:h-14 md:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
              />
            </Link>

            {/* Right Side Navigation & Action CTA */}
            <div className="flex items-center gap-2 xl:gap-4 ml-auto">
              {/* Desktop Navigation Links (Home hidden, single line whitespace-nowrap) */}
              <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1.5 whitespace-nowrap">
                {navigationLinks
                  .filter((link) => link.name !== 'Home')
                  .map((link) => {
                    if (link.dropdown) {
                      const isLinkActive = isActive(link.href);
                      return (
                        <div
                          key={link.name}
                          className="relative group"
                        >
                          <Link
                            to={link.href}
                            className={`flex items-center gap-1 px-2.5 xl:px-3 py-2 text-xs xl:text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-200 ${
                              isLinkActive
                                ? 'text-[#0F766E] font-semibold bg-[#F5FAF9]'
                                : 'text-[#123B5D] hover:text-[#0F766E] hover:bg-[#F5FAF9]'
                            }`}
                          >
                            <span>{link.name}</span>
                            <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180 text-[#64748B] flex-shrink-0" />
                          </Link>

                          {/* Dropdown Menu */}
                          <div className="absolute top-full right-0 w-80 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-50">
                            <div className="bg-white rounded-xl shadow-elevated border border-[#D9E8E5] p-2">
                              {link.dropdown.slice(0, 4).map((subItem) => (
                                <Link
                                  key={subItem.name}
                                  to={subItem.href}
                                  className="block p-2.5 rounded-lg hover:bg-[#F5FAF9] transition-colors group/item"
                                >
                                  <div className="text-xs font-semibold text-[#18323A] group-hover/item:text-[#0F766E]">
                                    {subItem.name}
                                  </div>
                                  {subItem.desc && (
                                    <div className="text-[11px] text-[#64748B] mt-0.5 line-clamp-1">
                                      {subItem.desc}
                                    </div>
                                  )}
                                </Link>
                              ))}
                              <div className="border-t border-[#D9E8E5] mt-1 pt-1">
                                <Link
                                  to={link.href}
                                  className="flex items-center justify-between p-2 rounded-lg text-xs font-semibold text-[#0F766E] hover:bg-[#F5FAF9] transition-colors group/viewall"
                                >
                                  <span>View All Services</span>
                                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/viewall:translate-x-1" />
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <Link
                        key={link.name}
                        to={link.href}
                        className={`px-2.5 xl:px-3 py-2 text-xs xl:text-sm font-medium rounded-lg whitespace-nowrap transition-colors duration-200 ${
                          isActive(link.href)
                            ? 'text-[#0F766E] font-semibold bg-[#F5FAF9]'
                            : 'text-[#123B5D] hover:text-[#0F766E] hover:bg-[#F5FAF9]'
                        }`}
                      >
                        {link.name}
                      </Link>
                    );
                  })}
              </nav>

              {/* Action CTA Button */}
              <Button
                onClick={onOpenConsultation}
                variant="primary"
                size="sm"
                className="hidden sm:inline-flex shadow-sm whitespace-nowrap flex-shrink-0 ml-2"
              >
                Book a Consultation
              </Button>

              {/* Mobile hamburger button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-[#123B5D] hover:bg-[#F5FAF9] focus:outline-none flex-shrink-0"
                aria-label="Toggle navigation menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile & Tablet Slide-Over Drawer Menu (Matching Reference Layout) */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 flex justify-end">
            {/* Dark Backdrop */}
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 animate-in fade-in"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Drawer Panel */}
            <div className="relative w-full max-w-sm sm:max-w-md bg-white h-full shadow-2xl flex flex-col z-50 animate-in slide-in-from-right duration-300">
              
              {/* Dark Top Header Bar */}
              <div className="bg-[#123B5D] text-white px-6 py-4 flex items-center justify-between border-b border-[#0F766E]/30">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3"
                >
                  <img
                    src="/umed logo.png"
                    alt="UMED Logo"
                    className="h-10 w-auto bg-white p-1 rounded-lg object-contain"
                  />
                  <div className="flex flex-col">
                    <span className="font-serif font-bold text-lg text-white leading-tight">
                      UMED
                    </span>
                    <span className="text-[10px] text-[#2A9D8F] font-semibold tracking-wider uppercase">
                      Rehabilitation Centre
                    </span>
                  </div>
                </Link>

                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-full text-white hover:bg-white/10 transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>

              {/* Drawer Body - Vertical Nav List */}
              <div className="flex-1 overflow-y-auto px-6 py-4 divide-y divide-slate-100">
                {navigationLinks.map((link) => {
                  const hasDropdown = Boolean(link.dropdown && link.dropdown.length > 0);
                  const isExpanded = Boolean(expandedDropdowns[link.name]);

                  if (hasDropdown) {
                    return (
                      <div key={link.name} className="py-3.5">
                        <button
                          type="button"
                          onClick={() => toggleDropdown(link.name)}
                          className="w-full flex items-center justify-between text-base sm:text-lg font-bold text-[#123B5D] hover:text-[#0F766E] transition-colors text-left"
                        >
                          <span>{link.name}</span>
                          <ChevronDown
                            className={`w-5 h-5 text-[#64748B] transition-transform duration-200 ${
                              isExpanded ? 'rotate-180 text-[#0F766E]' : ''
                            }`}
                          />
                        </button>

                        {/* Collapsed by default, expands on tap */}
                        {isExpanded && (
                          <div className="mt-3 pl-3 space-y-2.5 border-l-2 border-[#0F766E]/40 animate-in fade-in slide-in-from-top-2 duration-200">
                            {link.dropdown.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.href}
                                onClick={() => setMobileMenuOpen(false)}
                                className="block text-sm font-medium text-[#475569] hover:text-[#0F766E] py-1 transition-colors"
                              >
                                {subItem.name}
                              </Link>
                            ))}
                            <Link
                              to={link.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0F766E] hover:underline pt-1.5"
                            >
                              <span>View All Services</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <div key={link.name} className="py-3.5">
                      <Link
                        to={link.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block text-base sm:text-lg font-bold transition-colors ${
                          isActive(link.href)
                            ? 'text-[#0F766E]'
                            : 'text-[#123B5D] hover:text-[#0F766E]'
                        }`}
                      >
                        {link.name}
                      </Link>
                    </div>
                  );
                })}
              </div>

              {/* Drawer Bottom Action Section */}
              <div className="p-6 bg-[#F8FAFC] border-t border-slate-200 space-y-4">
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onOpenConsultation();
                  }}
                  className="w-full py-3.5 px-6 rounded-2xl bg-[#0F766E] hover:bg-[#123B5D] text-white font-bold text-base shadow-md transition-all duration-200 text-center active:scale-[0.98]"
                >
                  Book a Consultation
                </button>

                <div className="flex items-center justify-center">
                  <a
                    href={`tel:${contactData.phones[0].raw}`}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-[#475569] hover:text-[#0F766E] transition-colors"
                  >
                    <Phone className="w-4 h-4 text-[#0F766E]" />
                    <span>Contact: {contactData.phones[0].number}</span>
                  </a>
                </div>
              </div>

            </div>
          </div>
        )}
      </header>
    </>
  );
}
